/**
 * Wallet Service
 * Firestore-backed wallet with Stripe Connect integration.
 * Each user gets a wallet doc at wallets/{userId} with a transactions subcollection.
 */

import { db } from '@/lib/firebase';
import {
  collection, doc, getDoc, setDoc, updateDoc, getDocs,
  query, where, orderBy, limit, startAfter,
  Timestamp, onSnapshot, addDoc,
} from 'firebase/firestore';

// ═══════════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════════

export interface CryptoAddress {
  currency: string;     // 'btc', 'eth', 'sol', 'usdc', etc.
  address: string;
  network?: string;     // 'mainnet', 'polygon', 'solana', etc.
}

export interface UserWallet {
  userId: string;
  stripeConnectAccountId?: string;
  stripeOnboardingComplete: boolean;
  defaultCurrency: string;            // ISO 4217 e.g. 'usd', 'sek', 'gbp'

  balances: {
    available: number;    // cents
    pending: number;      // cents
    lifetime: number;     // cents — total earned all time
  };

  cryptoEnabled: boolean;
  cryptoAddresses: CryptoAddress[];
  acceptedCryptos: string[];

  payoutSchedule: 'manual' | 'daily' | 'weekly' | 'monthly';
  payoutThreshold: number;    // cents — minimum for auto-payout

  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export type TransactionType = 'sale' | 'download' | 'stream' | 'tip' | 'payout' | 'refund' | 'split_income' | 'platform_credit';
export type TransactionStatus = 'pending' | 'completed' | 'failed' | 'refunded';
export type PaymentMethod = 'stripe' | 'alipay' | 'wechat' | 'crypto';

export interface WalletTransaction {
  id?: string;
  userId: string;
  type: TransactionType;
  amount: number;           // cents (gross)
  currency: string;
  platformFee: number;      // cents (5% of gross)
  netAmount: number;        // amount - platformFee

  sourceType: 'marketplace' | 'download' | 'stream' | 'tip' | 'manual';
  sourceId?: string;
  sourceTitle?: string;

  // Split info (for split_income type)
  parentTransactionId?: string;
  splitPercent?: number;
  originalOwnerId?: string;

  paymentMethod: PaymentMethod;
  stripePaymentIntentId?: string;
  stripeTransferId?: string;

  status: TransactionStatus;
  createdAt: Timestamp;
  note?: string;
}

// ═══════════════════════════════════════════════════════════════════════════════
// Constants
// ═══════════════════════════════════════════════════════════════════════════════

export const PLATFORM_FEE_PERCENT = 5; // 5%

export const DEFAULT_WALLET: Omit<UserWallet, 'userId' | 'createdAt' | 'updatedAt'> = {
  stripeOnboardingComplete: false,
  defaultCurrency: 'usd',
  balances: { available: 0, pending: 0, lifetime: 0 },
  cryptoEnabled: false,
  cryptoAddresses: [],
  acceptedCryptos: [],
  payoutSchedule: 'manual',
  payoutThreshold: 1000, // $10 min
};

export const SUPPORTED_CRYPTOS = [
  { id: 'btc', name: 'Bitcoin', symbol: 'BTC', network: 'bitcoin' },
  { id: 'eth', name: 'Ethereum', symbol: 'ETH', network: 'ethereum' },
  { id: 'sol', name: 'Solana', symbol: 'SOL', network: 'solana' },
  { id: 'usdc', name: 'USD Coin', symbol: 'USDC', network: 'ethereum' },
  { id: 'usdt', name: 'Tether', symbol: 'USDT', network: 'ethereum' },
  { id: 'matic', name: 'Polygon', symbol: 'MATIC', network: 'polygon' },
  { id: 'xrp', name: 'Ripple', symbol: 'XRP', network: 'xrpl' },
  { id: 'doge', name: 'Dogecoin', symbol: 'DOGE', network: 'dogecoin' },
  { id: 'ltc', name: 'Litecoin', symbol: 'LTC', network: 'litecoin' },
  { id: 'ada', name: 'Cardano', symbol: 'ADA', network: 'cardano' },
  { id: 'avax', name: 'Avalanche', symbol: 'AVAX', network: 'avalanche' },
  { id: 'dot', name: 'Polkadot', symbol: 'DOT', network: 'polkadot' },
];

export const SUPPORTED_FIAT_CURRENCIES = [
  'usd', 'eur', 'gbp', 'sek', 'nok', 'dkk', 'jpy', 'krw', 'cad', 'aud',
  'nzd', 'chf', 'brl', 'mxn', 'ngn', 'inr', 'cny', 'hkd', 'sgd', 'zar',
];

// ═══════════════════════════════════════════════════════════════════════════════
// Wallet CRUD
// ═══════════════════════════════════════════════════════════════════════════════

/** Get or create a wallet for a user */
export async function getOrCreateWallet(userId: string): Promise<UserWallet> {
  const ref = doc(db, 'wallets', userId);
  const snap = await getDoc(ref);

  if (snap.exists()) {
    return { userId, ...snap.data() } as UserWallet;
  }

  // Create default wallet
  const now = Timestamp.now();
  const wallet: UserWallet = {
    userId,
    ...DEFAULT_WALLET,
    createdAt: now,
    updatedAt: now,
  };
  await setDoc(ref, wallet);
  return wallet;
}

/** Get wallet (returns null if not created yet) */
export async function getWallet(userId: string): Promise<UserWallet | null> {
  const ref = doc(db, 'wallets', userId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return { userId, ...snap.data() } as UserWallet;
}

/** Update wallet fields */
export async function updateWallet(
  userId: string,
  updates: Partial<Omit<UserWallet, 'userId' | 'createdAt'>>,
): Promise<void> {
  const ref = doc(db, 'wallets', userId);
  await updateDoc(ref, {
    ...updates,
    updatedAt: Timestamp.now(),
  });
}

/**
 * @deprecated Server-only. firestore.rules now rejects browser writes to `stripeConnectAccountId`
 * (it decides where a user's sales are routed). /api/stripe-connect/onboard records it.
 */
export async function saveStripeConnectAccount(
  userId: string,
  stripeConnectAccountId: string,
): Promise<void> {
  await updateWallet(userId, { stripeConnectAccountId });
}

/** @deprecated Server-only — flipped by /api/stripe-connect/status and the account.updated webhook. */
export async function markStripeOnboardingComplete(userId: string): Promise<void> {
  await updateWallet(userId, { stripeOnboardingComplete: true });
}

/** @deprecated Server-only. Browser writes to `balances` are rejected by firestore.rules. */
export async function updateWalletBalances(
  userId: string,
  available: number,
  pending: number,
  addToLifetime: number = 0,
): Promise<void> {
  const ref = doc(db, 'wallets', userId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;
  const current = snap.data() as UserWallet;

  await updateDoc(ref, {
    'balances.available': available,
    'balances.pending': pending,
    'balances.lifetime': (current.balances?.lifetime || 0) + addToLifetime,
    updatedAt: Timestamp.now(),
  });
}

/** Update crypto preferences */
export async function updateCryptoSettings(
  userId: string,
  cryptoEnabled: boolean,
  cryptoAddresses: CryptoAddress[],
  acceptedCryptos: string[],
): Promise<void> {
  await updateWallet(userId, {
    cryptoEnabled,
    cryptoAddresses,
    acceptedCryptos,
  });
}

/** Update payout preferences */
export async function updatePayoutPreferences(
  userId: string,
  payoutSchedule: UserWallet['payoutSchedule'],
  payoutThreshold: number,
  defaultCurrency: string,
): Promise<void> {
  await updateWallet(userId, {
    payoutSchedule,
    payoutThreshold,
    defaultCurrency,
  });
}

// ═══════════════════════════════════════════════════════════════════════════════
// Transactions
// ═══════════════════════════════════════════════════════════════════════════════

/** @deprecated Server-only. Wallet transaction rows are written by the webhook/payout routes; the browser is denied by firestore.rules. */
export async function addTransaction(
  userId: string,
  tx: Omit<WalletTransaction, 'id' | 'createdAt'>,
): Promise<string> {
  const txRef = await addDoc(
    collection(db, 'wallets', userId, 'transactions'),
    {
      ...tx,
      createdAt: Timestamp.now(),
    },
  );
  return txRef.id;
}

/** Get transactions for a user (paginated) */
export async function getWalletTransactions(
  userId: string,
  opts?: {
    type?: TransactionType;
    status?: TransactionStatus;
    limitCount?: number;
    afterDoc?: any;
  },
): Promise<WalletTransaction[]> {
  const constraints: any[] = [orderBy('createdAt', 'desc')];

  if (opts?.type) constraints.unshift(where('type', '==', opts.type));
  if (opts?.status) constraints.unshift(where('status', '==', opts.status));
  constraints.push(limit(opts?.limitCount || 50));
  if (opts?.afterDoc) constraints.push(startAfter(opts.afterDoc));

  const q = query(collection(db, 'wallets', userId, 'transactions'), ...constraints);
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as WalletTransaction));
}

/** Get all transactions across all wallets (admin) */
export async function getAllTransactions(
  opts?: { limitCount?: number },
): Promise<(WalletTransaction & { walletUserId: string })[]> {
  // For admin overview — queries the collectionGroup
  // Note: requires a collectionGroup index on 'transactions'
  const { collectionGroup } = await import('firebase/firestore');
  const q = query(
    collectionGroup(db, 'transactions'),
    orderBy('createdAt', 'desc'),
    limit(opts?.limitCount || 100),
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => {
    const walletUserId = d.ref.parent.parent?.id || '';
    return { id: d.id, walletUserId, ...d.data() } as WalletTransaction & { walletUserId: string };
  });
}

// ═══════════════════════════════════════════════════════════════════════════════
// Real-time Subscriptions
// ═══════════════════════════════════════════════════════════════════════════════

/** Subscribe to wallet changes */
export function subscribeWallet(
  userId: string,
  callback: (wallet: UserWallet | null) => void,
): () => void {
  return onSnapshot(doc(db, 'wallets', userId), (snap) => {
    if (!snap.exists()) { callback(null); return; }
    callback({ userId, ...snap.data() } as UserWallet);
  });
}

/** Subscribe to transactions in real-time */
export function subscribeTransactions(
  userId: string,
  callback: (txs: WalletTransaction[]) => void,
  limitCount: number = 20,
): () => void {
  const q = query(
    collection(db, 'wallets', userId, 'transactions'),
    orderBy('createdAt', 'desc'),
    limit(limitCount),
  );
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() } as WalletTransaction)));
  });
}

// ═══════════════════════════════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════════════════════════════

/** Calculate platform fee and net amount */
export function calculateFees(grossAmount: number): {
  platformFee: number;
  netAmount: number;
} {
  const platformFee = Math.round(grossAmount * (PLATFORM_FEE_PERCENT / 100));
  return { platformFee, netAmount: grossAmount - platformFee };
}

/** Format cents to display currency */
export function formatWalletAmount(cents: number, currency: string = 'usd'): string {
  const amount = cents / 100;
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${currency.toUpperCase()} ${amount.toFixed(2)}`;
  }
}

/** Get all wallets (admin) */
export async function getAllWallets(): Promise<UserWallet[]> {
  const snap = await getDocs(collection(db, 'wallets'));
  return snap.docs.map(d => ({ userId: d.id, ...d.data() } as UserWallet));
}
